import React, { useState, useRef, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BottomNavItem } from "@maeiz/ui";
import {
    Home,
    History,
    Settings,
    Sun,
    Moon,
    Globe,
    Banknote,
    ChevronDown,
    LogOut,
} from "lucide-react";
import { NotificationBell } from "@features/notifications/components/NotificationBell";
import { useThemeStore } from "@stores/theme";
import { useLanguageStore, type LanguageCode } from "@stores/language";
import { useCurrencyStore, type CurrencyCode } from "@stores/currency";
import { useLogout } from "@features/auth/hooks/useLogout";

const TABS = [
    { to: "/", labelKey: "nav.home", icon: Home },
    { to: "/history", labelKey: "nav.history", icon: History },
    { to: "/settings", labelKey: "nav.settings", icon: Settings },
] as const;

const LANGUAGES: { code: LanguageCode; label: string; flag: string }[] = [
    { code: "en", label: "English", flag: "EN" },
    { code: "ur", label: "اردو", flag: "UR" },
    { code: "ar-AE", label: "العربية", flag: "AR" },
];

const CURRENCIES: { code: CurrencyCode; label: string; symbol: string }[] = [
    { code: "PKR", label: "PKR", symbol: "Rs" },
    { code: "AED", label: "AED", symbol: "د.إ" },
    { code: "USD", label: "USD", symbol: "$" },
];

const CustomerHeader: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const theme = useThemeStore((s) => s.theme);
    const toggleTheme = useThemeStore((s) => s.toggleTheme);
    const { logout, isLoggingOut } = useLogout();

    const language = useLanguageStore((s) => s.language);
    const setLanguage = useLanguageStore((s) => s.setLanguage);

    const currency = useCurrencyStore((s) => s.currency);
    const setCurrency = useCurrencyStore((s) => s.setCurrency);

    const [isLangOpen, setIsLangOpen] = useState(false);
    const [isCurrOpen, setIsCurrOpen] = useState(false);

    const langRef = useRef<HTMLDivElement>(null);
    const currRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (langRef.current && !langRef.current.contains(e.target as Node)) {
                setIsLangOpen(false);
            }
            if (currRef.current && !currRef.current.contains(e.target as Node)) {
                setIsCurrOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
    const currentCurr = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

    return (
        <header className="w-full bg-white/90 dark:bg-neutral-ink/90 backdrop-blur-xl border-b border-neutral-border dark:border-white/[0.08] transition-colors app-header-container hidden lg:block">
            <div className="w-full px-4 sm:px-8 lg:px-[100px] py-2.5 flex items-center justify-between gap-4">
                {/* Brand Logo & Desktop Nav */}
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="flex items-center gap-2 font-display text-start focus:outline-none"
                    >
                        <span className="text-xl font-black tracking-widest text-neutral-ink dark:text-white">
                            MAEIZ<span className="text-gold">.</span>
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-mono font-bold uppercase tracking-wider bg-primary/10 text-primary dark:bg-gold/15 dark:text-gold-light border border-primary/20 dark:border-gold/30">
                            PORTAL
                        </span>
                    </button>

                    {/* Desktop Navigation Links */}
                    <nav className="hidden lg:flex items-center gap-1 ms-4 border-s border-neutral-border dark:border-white/10 ps-4">
                        {TABS.map((tab) => {
                            const isActive = pathname === tab.to;
                            return (
                                <button
                                    key={tab.to}
                                    type="button"
                                    onClick={() => navigate(tab.to)}
                                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors duration-150 ${
                                        isActive
                                            ? "bg-gold/15 text-gold-dark dark:text-gold-light border border-gold/30 shadow-sm"
                                            : "text-neutral-secondary dark:text-slate-300 hover:bg-neutral-light/70 dark:hover:bg-white/[0.06]"
                                    }`}
                                >
                                    <tab.icon size={15} />
                                    <span>{t(tab.labelKey)}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Right controls */}
                <div className="flex items-center gap-2 sm:gap-3">
                    {/* Language Dropdown */}
                    <div className="relative" ref={langRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setIsLangOpen((v) => !v);
                                setIsCurrOpen(false);
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-neutral-border dark:border-white/10 text-xs font-bold text-neutral-ink dark:text-white hover:bg-neutral-light/70 dark:hover:bg-white/10 transition-colors shadow-sm"
                            title="Select Language"
                        >
                            <Globe size={14} className="text-gold" />
                            <span className="font-mono uppercase">{currentLang.flag}</span>
                            <ChevronDown size={12} className="opacity-60" />
                        </button>

                        {isLangOpen && (
                            <div className="absolute end-0 mt-2 w-36 rounded-2xl border border-neutral-border bg-white shadow-mz-lg dark:border-white/10 dark:bg-neutral-navy backdrop-blur-2xl py-1 z-50">
                                {LANGUAGES.map((item) => (
                                    <button
                                        key={item.code}
                                        type="button"
                                        onClick={() => {
                                            setLanguage(item.code);
                                            setIsLangOpen(false);
                                        }}
                                        className={`w-full flex items-center justify-between px-3.5 py-2 text-xs font-semibold transition-colors ${
                                            language === item.code
                                                ? "bg-gold/15 text-gold-dark dark:text-gold-light font-bold"
                                                : "text-neutral-ink dark:text-white hover:bg-neutral-light/50 dark:hover:bg-white/5"
                                        }`}
                                    >
                                        <span>{item.label}</span>
                                        <span className="text-[10px] font-mono text-neutral-secondary">
                                            {item.flag}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Currency Dropdown */}
                    <div className="relative" ref={currRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setIsCurrOpen((v) => !v);
                                setIsLangOpen(false);
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-neutral-border dark:border-white/10 text-xs font-bold text-neutral-ink dark:text-white hover:bg-neutral-light/70 dark:hover:bg-white/10 transition-colors shadow-sm"
                            title="Select Currency"
                        >
                            <Banknote size={14} className="text-gold" />
                            <span className="font-mono">{currentCurr.label}</span>
                            <ChevronDown size={12} className="opacity-60" />
                        </button>

                        {isCurrOpen && (
                            <div className="absolute end-0 mt-2 w-36 rounded-2xl border border-neutral-border bg-white shadow-mz-lg dark:border-white/10 dark:bg-neutral-navy backdrop-blur-2xl py-1 z-50">
                                {CURRENCIES.map((item) => (
                                    <button
                                        key={item.code}
                                        type="button"
                                        onClick={() => {
                                            setCurrency(item.code);
                                            setIsCurrOpen(false);
                                        }}
                                        className={`w-full flex items-center justify-between px-3.5 py-2 text-xs font-semibold transition-colors ${
                                            currency === item.code
                                                ? "bg-gold/15 text-gold-dark dark:text-gold-light font-bold"
                                                : "text-neutral-ink dark:text-white hover:bg-neutral-light/50 dark:hover:bg-white/5"
                                        }`}
                                    >
                                        <span>{item.label}</span>
                                        <span className="text-[10px] font-mono text-gold">
                                            {item.symbol}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Dark Mode Toggle */}
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label="Toggle Theme"
                        title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        className="rounded-xl p-2 text-neutral-ink dark:text-white hover:bg-neutral-light/70 dark:hover:bg-white/10 border border-neutral-border dark:border-white/10 transition-colors shadow-sm"
                    >
                        {theme === "dark" ? (
                            <Sun size={18} className="text-gold-light" />
                        ) : (
                            <Moon
                                size={18}
                                className="text-neutral-secondary hover:text-neutral-ink"
                            />
                        )}
                    </button>

                    {/* Notification Bell */}
                    <NotificationBell />

                    {/* Logout Button */}
                    <button
                        type="button"
                        onClick={logout}
                        disabled={isLoggingOut}
                        aria-label={t("settings.logout")}
                        title={t("settings.logout")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-danger/40 text-xs font-bold text-danger hover:bg-danger/10 transition-colors shadow-sm disabled:opacity-50"
                    >
                        <LogOut size={15} />
                        <span className="hidden xl:inline">{t("settings.logout")}</span>
                    </button>
                </div>
            </div>
        </header>
    );
};

export const CustomerLayout: React.FC = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { t } = useTranslation();

    return (
        <div className="flex min-h-screen flex-col bg-neutral-light text-neutral-dark dark:bg-neutral-ink dark:text-white transition-colors">
            {/* Top Header: SHOWN after 990px screens, HIDDEN at/below 990px */}
            <div className="app-header-container sticky top-0 z-30">
                <CustomerHeader />
            </div>

            {/* Main Content Area */}
            <main className="flex-1 main-content-layout">
                <Outlet />
            </main>

            {/* Bottom Nav: HIDDEN after 990px screens, SHOWN at/below 990px */}
            <nav
                className="bottom-nav-container fixed inset-x-0 bottom-0 z-40 border-t border-neutral-border bg-white/95 backdrop-blur-2xl dark:border-white/[0.08] dark:bg-neutral-ink/95 shadow-mz-md"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
                <div className="mx-auto flex max-w-screen-xl items-stretch justify-around">
                    {TABS.map((tab) => (
                        <BottomNavItem
                            key={tab.to}
                            icon={<tab.icon size={20} />}
                            label={t(tab.labelKey)}
                            active={pathname === tab.to}
                            onClick={() => navigate(tab.to)}
                        />
                    ))}
                </div>
            </nav>
        </div>
    );
};
